import { useState } from 'react';
import {
  useGetNotifications,
  useSetNotificationsReaded,
} from '../data/hooks/notifications';
import { Notification } from '../models/notification';
import { LocalStorageKeys } from '../utils/enums';
import { useLocalStorage } from './use-local-storage';

export type NotificationsState = Notification[];

export const useNotifications = () => {
  const { setItem, getItem, removeItem } = useLocalStorage();
  const { fetch: getNotificationFetcher } = useGetNotifications();
  const { fetch: setNotificationsReadedFetcher } = useSetNotificationsReaded();

  const [notifications, setNotifications] = useState<NotificationsState>(() => {
    const stored = getItem(LocalStorageKeys.NOTIFICATIONS);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const listNotifications = async () => {
    setPage(1);
    setHasMore(true);
    await getNotificationFetcher({
      params: { page: 1, limit: 5 },
      onSuccess: (notList) => {
        const list = notList ?? [];
        set(list);
        if (list.length < 5) {
          setHasMore(false);
        }
      },
    });
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    await getNotificationFetcher({
      params: { page: nextPage, limit: 5 },
      onSuccess: (notList) => {
        const list = notList ?? [];
        if (list.length < 5) {
          setHasMore(false);
        }
        if (list.length > 0) {
          setNotifications((prev) => {
            const merged = [...prev];
            for (const item of list) {
              if (!merged.some((n) => n.id === item.id)) {
                merged.push(item);
              }
            }
            const sorted = merged.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            );
            setItem(LocalStorageKeys.NOTIFICATIONS, JSON.stringify(sorted));
            return sorted;
          });
          setPage(nextPage);
        }
        setLoadingMore(false);
      },
      onError: () => {
        setLoadingMore(false);
      },
    });
  };

  const setNotificationsReaded = async (ids: number[]) => {
    await setNotificationsReadedFetcher({
      data: { ids },
      onSuccess: () => {
        setNotifications((prev) => {
          const filtered = prev.filter((n) => !ids.includes(n.id));
          setItem(LocalStorageKeys.NOTIFICATIONS, JSON.stringify(filtered));
          return filtered;
        });
      },
    });
  };

  const set = (
    value:
      | NotificationsState
      | ((prev: NotificationsState) => NotificationsState),
  ) => {
    if (typeof value === 'function') {
      setNotifications((prev) => {
        const nextVal = value(prev);
        setItem(LocalStorageKeys.NOTIFICATIONS, JSON.stringify(nextVal));
        return nextVal;
      });
    } else {
      setItem(LocalStorageKeys.NOTIFICATIONS, JSON.stringify(value));
      setNotifications(value);
    }
  };

  const remove = () => {
    removeItem(LocalStorageKeys.NOTIFICATIONS);
    setNotifications([]);
    setPage(1);
    setHasMore(false);
  };

  return {
    set,
    remove,
    listNotifications,
    setNotificationsReaded,
    notifications,
    page,
    hasMore,
    loadingMore,
    loadMore,
  };
};
